<template>
  <v-container fluid class="pa-5 seller-page">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center">
        <v-progress-circular indeterminate size="56" width="4" color="#F4811F" />
        <span class="mt-4 white--text font-weight-medium">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <!-- Page Header -->
    <div class="page-header mb-6">
      <div>
        <h1 class="page-title">จัดการออเดอร์</h1>
        <p class="page-subtitle">{{ filteredOrders.length }} รายการทั้งหมด</p>
      </div>
    </div>

    <!-- Filter card -->
    <div class="s-card mb-5">
      <div class="s-card-body">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field v-model="search" placeholder="ค้นหาออเดอร์ (รหัส, ชื่อผู้ซื้อ, ชื่อสินค้า)" outlined dense
              prepend-inner-icon="mdi-magnify" hide-details="auto" clearable background-color="#f9fafb"
              class="rounded-lg" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="selectedStatus" :items="statusOptions" item-text="label" item-value="value"
              placeholder="กรองตามสถานะการจัดส่ง" outlined dense hide-details="auto" clearable
              background-color="#f9fafb" class="rounded-lg" />
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Table card -->
    <div class="s-card">
      <div class="s-card-header">
        <div class="s-card-icon" style="background:#fff7ed">
          <v-icon color="#F4811F" size="18">mdi-receipt-text-outline</v-icon>
        </div>
        <span class="s-card-title">รายการออเดอร์</span>
        <span class="s-card-badge">{{ filteredOrders.length }} รายการ</span>
      </div>
      <div class="s-card-body" style="padding:0">
        <template v-if="filteredOrders.length > 0">
          <OrderTable :orders="filteredOrders" :vehicle-types="shippingCompanies" @view="handleView"
            @cancel="handleCancelOrder" @cancel-seeking="handleCancelSeekingRider" @update-status="handleUpdateStatus"
            @call-rider="handleCallRider" @load-orders="loadOrders" />
        </template>
        <div v-else class="empty-state">
          <img src="@/assets/not_exist_search.svg" alt="no-data" width="88" height="88" />
          <p class="empty-text">{{ orders.length === 0 ? "ยังไม่มีรายการออเดอร์" : "ไม่พบออเดอร์ที่ค้นหา" }}</p>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import OrderTable from "@/components/SellerOrders/Table.vue";
import { getMyOrdersApi } from "@/services/api/order";
import { callRiderApi, cancelOrderBySellerApi, cancelSeekingRiderApi } from "@/services/api/sellerServices";
import { getShippingCompaniesApiSeller } from "@/services/api/adminServices";
import swalAlert from "@/services/alert/swalServices";
import Swal from "sweetalert2";

export default {
  name: "SellerOrders",
  components: { OrderTable },
  data() {
    return {
      isLoading: false,
      orders: [],
      shippingCompanies: [],
      search: "",
      selectedStatus: null,
      statusOptions: [
        { label: "รอจัดส่ง", value: "pending" },
        { label: "กำลังเตรียม", value: "processing" },
        { label: "จัดส่งแล้ว", value: "shipped" },
        { label: "ยกเลิก", value: "cancelled" },
      ],
    };
  },
  computed: {
    filteredOrders() {
      let result = [...this.orders];
      if (this.selectedStatus) result = result.filter((o) => (o.shipStatus || "").toLowerCase() === this.selectedStatus);
      if ((this.search || "").trim()) {
        const kw = this.search.toLowerCase().trim();
        result = result.filter((o) => {
          const buyerName = this.getBuyerName(o.buyer).toLowerCase();
          const productNames = (o.items || []).map((i) => (i.productName || "").toLowerCase()).join(" ");
          return (o.id || "").toLowerCase().includes(kw) || (o.orderNumber || "").toLowerCase().includes(kw) || buyerName.includes(kw) || (o.buyer?.email || "").toLowerCase().includes(kw) || productNames.includes(kw);
        });
      }
      return result;
    },
  },
  created() { this.loadOrders(); this.loadShippingCompanies(); },
  methods: {
    async loadOrders() {
      this.isLoading = true;
      try {
        const res = await getMyOrdersApi();
        this.orders = res?.data || [];
      } catch (err) {
        swalAlert.Error("โหลดข้อมูลไม่สำเร็จ", err?.response?.data?.message || "ไม่สามารถโหลดข้อมูลออเดอร์ได้");
        this.orders = [];
      } finally { this.isLoading = false; }
    },
    async loadShippingCompanies() {
      try {
        const res = await getShippingCompaniesApiSeller();
        const raw = (Array.isArray(res) && res) || (Array.isArray(res.data) && res.data) || (Array.isArray(res.items) && res.items) || [];
        this.shippingCompanies = raw.map((c) => ({ id: c.id || c._id, name: c.name || "-", link: c.link || c.trackingUrlTemplate || "", shipId: c.shipId || null }));
      } catch { this.shippingCompanies = []; }
    },
    getBuyerName(buyer) {
      if (!buyer) return "";
      return (`${buyer.prefix || ""} ${buyer.firstName || ""} ${buyer.lastName || ""}`).trim() || buyer.username || "";
    },
    handleView(order) { this.$router.push({ name: "seller_order_detail", params: { id: order.id } }); },
    async handleCancelSeekingRider(order) {
      try {
        const result = await swalAlert.Confirm("ต้องการยกเลิกการหาไรเดอร์สำหรับออเดอร์นี้ใช่หรือไม่?");
        if (!result.isConfirmed) return;
        this.isLoading = true;
        await cancelSeekingRiderApi(order.id);
        await swalAlert.Success("ยกเลิกการหาไรเดอร์สำเร็จ");
        this.loadOrders();
      } catch (err) {
        swalAlert.Error("ยกเลิกไม่สำเร็จ", err?.response?.data?.message || "ไม่สามารถยกเลิกการหาไรเดอร์ได้");
      } finally {
        this.isLoading = false;
      }
    },
    async handleCancelOrder(order) {
      const orderNo = order.orderNumber || order.id?.slice(-8).toUpperCase();
      const { isConfirmed, value: cancelReason } = await Swal.fire({
        title: "ยกเลิกออเดอร์",
        iconHtml: '<i class="mdi mdi-alert-circle-outline" style="color:#ef4444;font-size:48px"></i>',
        html: `
          <div style="text-align:left;padding:0 4px">
            <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:10px 12px;margin-bottom:14px;font-size:13px;color:#991b1b">
              <strong>ออเดอร์ #${orderNo}</strong> จะถูกยกเลิก และระบบจะคืนเงินให้ผู้ซื้อโดยอัตโนมัติ (กรณีชำระเงินแล้ว)
            </div>
            <label for="swal-cancel-reason" style="display:block;font-weight:600;color:#374151;margin-bottom:6px;font-size:14px">
              หมายเหตุการยกเลิก <span style="color:#9ca3af;font-weight:400">(ผู้ซื้อจะเห็นข้อความนี้)</span>
            </label>
            <textarea id="swal-cancel-reason" class="swal2-textarea"
              placeholder="เช่น สินค้าหมดสต็อก, ไม่สามารถจัดส่งได้ในช่วงนี้..."
              maxlength="300" rows="4"
              style="width:100%;margin:0;resize:vertical;font-size:14px;padding:10px;border:1px solid #d1d5db;border-radius:8px;box-shadow:none"></textarea>
            <div id="swal-cancel-counter" style="text-align:right;font-size:12px;color:#9ca3af;margin-top:4px">0 / 300</div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "ยืนยันยกเลิกออเดอร์",
        cancelButtonText: "ไม่ยกเลิก",
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        reverseButtons: true,
        focusCancel: true,
        width: 520,
        didOpen: () => {
          const ta = document.getElementById("swal-cancel-reason");
          const counter = document.getElementById("swal-cancel-counter");
          if (ta && counter) {
            ta.addEventListener("input", () => {
              counter.textContent = `${ta.value.length} / 300`;
            });
          }
        },
        preConfirm: () => {
          const val = document.getElementById("swal-cancel-reason")?.value?.trim() || "";
          if (!val) {
            Swal.showValidationMessage("กรุณาระบุเหตุผลการยกเลิก");
            return false;
          }
          return val;
        },
      });
      if (!isConfirmed) return;
      try {
        this.isLoading = true;
        await cancelOrderBySellerApi(order.id, cancelReason);
        await swalAlert.Success("ยกเลิกออเดอร์สำเร็จ");
        this.loadOrders();
      } catch (err) {
        swalAlert.Error("ยกเลิกไม่สำเร็จ", err?.response?.data?.message || "ไม่สามารถยกเลิกออเดอร์ได้");
      } finally {
        this.isLoading = false;
      }
    },
    async handleCallRider(order) {
      try {
        const result = await swalAlert.Confirm("คุณต้องการเรียกไรเดอร์สำหรับออเดอร์นี้ใช่หรือไม่?");
        if (!result.isConfirmed) return;
        this.isLoading = true;
        await callRiderApi(order.id);
        await swalAlert.Success("เรียกไรเดอร์สำเร็จ", "ระบบกำลังประกาศหาไรเดอร์เพื่อเข้ารับสินค้า");
        this.loadOrders();
      } catch (err) {
        swalAlert.Error("เรียกไรเดอร์ไม่สำเร็จ", err?.response?.data?.message || "ระบบไม่สามารถเรียกไรเดอร์ได้");
      } finally { this.isLoading = false; }
    },
    handleUpdateStatus(order) { console.log("Update order status:", order); },
  },
};
</script>

<style scoped>
@media (max-width: 599px) {
  .s-card-body { overflow-x: auto; }
}
</style>
